import styled from "styled-components"
import { Save, Download, Upload, FolderOpen, ZoomIn, ZoomOut, Users, Palette, FilePlus, User as UserIcon, LogOut, CalendarDays, Bell, User as PersonIcon, Users as TeamIcon, Home, Menu, MoreVertical, UserPlus, Plus, Search, X } from "lucide-react"
import { useRef, useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import PresenceBar from "./PresenceBar"

const HeaderContainer = styled.div`
  border-bottom: 1px solid #e5e7eb;
  background-color: white;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  height: 72px;
  box-sizing: border-box;
  padding: 0 14px;
  gap: 12px;
  border-bottom: 1px solid #f1f5f9;

  @media (max-width: 900px) {
    height: 64px;
    padding: 0 10px;
    gap: 8px;
    grid-template-columns: 1fr auto auto;
  }
`

const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 900px) {
    gap: 6px;
  }
`

const TopCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 900px) {
    display: none;
  }
`

const TopRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 14px;
  gap: 12px;
  background: #fafafa;
  min-height: 48px;

  @media (max-width: 900px) {
    padding: 6px 10px;
    gap: 6px;
  }
`

const ProjectTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  .title {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 360px;
    @media (max-width: 900px) { max-width: 180px; font-size: 14px; }
  }
`

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    gap: 4px;
  }
`

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }

  /* 태블릿/임베디드에서부터 라벨 숨기고 아이콘만 — wrap 방지 */
  @media (max-width: 1100px) {
    padding: 8px 10px;
    .btn-label {
      display: none;
    }
  }
  @media (max-width: 900px) {
    padding: 8px;
  }
`

const HamburgerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  color: #1f2937;
  flex-shrink: 0;

  &:hover { background: #f9fafb; }
`

const MoreMenuWrap = styled.div`
  position: relative;
  display: none;

  @media (max-width: 1100px) {
    display: inline-block;
  }
`

const MoreMenuBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  color: #1f2937;

  &:hover { background: #f9fafb; }
`

const MoreMenuDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 200px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.12);
  z-index: 120;
  overflow: hidden;

  button {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border: none;
    background: white;
    text-align: left;
    cursor: pointer;
    font-size: 13px;
    color: #1f2937;

    &:hover { background: #f3f4f6; }
    & + button { border-top: 1px solid #f3f4f6; }
  }
`

const DesktopOnly = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 1100px) {
    display: none;
  }
`

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }
`

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;

  /* 태블릿/임베디드: zoom 슬라이더는 자리 차지가 커서 숨김. 일단위/기준일/아바타는 유지 */
  @media (max-width: 1100px) {
    gap: 10px;
    & > *:first-child {  /* ZoomControls는 첫번째 */
      display: none;
    }
  }

  /* 모바일: 아바타만 남기고 모두 숨김 */
  @media (max-width: 900px) {
    gap: 6px;
    & > *:not(:last-child) {
      display: none;
    }
  }
`

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Slider = styled.input.attrs({ type: "range" })`
  width: 128px;
  height: 6px;
  -webkit-appearance: none;
  background: #e5e7eb;
  border-radius: 9999px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #2563eb;
    cursor: pointer;
  }
`

const Select = styled.select`
  width: 128px;
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background-color: white;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`

const ViewTabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
`

const ViewTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 14px;
  font-size: 13.5px;
  font-weight: ${(p) => (p.$active ? 700 : 600)};
  color: ${(p) => (p.$active ? "#0f172a" : "#64748b")};
  background: ${(p) => (p.$active ? "white" : "transparent")};
  border: none;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;
  letter-spacing: -0.01em;
  box-shadow: ${(p) => (p.$active ? "0 1px 2px rgba(15, 23, 42, 0.08)" : "none")};

  &:hover { color: #0f172a; }

  @media (max-width: 900px) {
    padding: 0 10px;
    font-size: 13px;
  }
`

const ShareButton = styled(Button)`
  background-color: #2563eb;
  color: white;
  border-color: #2563eb;

  &:hover {
    background-color: #1d4ed8;
  }
`

const SearchBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  background: #f1f5f9;
  border: 1px solid transparent;
  border-radius: 8px;
  width: 240px;
  transition: background 0.12s, border-color 0.12s, box-shadow 0.12s;

  &:focus-within {
    background: white;
    border-color: #bfdbfe;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  svg.icon { color: #94a3b8; flex-shrink: 0; }

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #0f172a;
    min-width: 0;

    &::placeholder { color: #94a3b8; }
  }

  button.clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: none;
    border-radius: 50%;
    background: #cbd5e1;
    color: white;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;

    &:hover { background: #94a3b8; }
  }

  @media (max-width: 1200px) { width: 180px; }
  @media (max-width: 900px) { display: none; }
`

const NewTaskCTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 13.5px;
  font-weight: 600;
  color: white;
  background: #2563eb;
  border: 1px solid #2563eb;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  letter-spacing: -0.01em;
  flex-shrink: 0;

  &:hover { background: #1d4ed8; border-color: #1d4ed8; }
  &:active { background: #1e40af; }

  @media (max-width: 900px) {
    padding: 8px 10px;
    .cta-label { display: none; }
  }
`

const UserMenuWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`

const AvatarButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 14px 4px 4px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;

  &:hover {
    border-color: #2563eb;
    background-color: #f9fafb;
  }
`

const AvatarCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #2563eb;
  color: white;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const UserNameLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 900px) {
    display: none;
  }
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 180px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  z-index: 100;
  overflow: hidden;
`

const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: none;
  background-color: transparent;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  text-align: left;

  &:hover {
    background-color: #f3f4f6;
  }

  & + & {
    border-top: 1px solid #f3f4f6;
  }
`

const DropdownHeader = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
  }
  span {
    font-size: 12px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

function UserMenu() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [open])

  if (!user) return null

  const displayName = user.userName || user.alias || user.userId || "사용자"
  const avatarSrc = user.profileImage || user.avatar_url || "/resource/defaultImg.png"

  return (
    <UserMenuWrapper ref={wrapperRef}>
      <AvatarButton onClick={() => setOpen((v) => !v)}>
        <AvatarCircle>
          <img src={avatarSrc} alt={displayName} />
        </AvatarCircle>
        <UserNameLabel>{displayName}</UserNameLabel>
      </AvatarButton>
      {open && (
        <Dropdown>
          <DropdownHeader>
            <strong>{displayName}</strong>
            {user.email && <span>{user.email}</span>}
          </DropdownHeader>
          <DropdownItem
            onClick={() => {
              setOpen(false)
              navigate("/mypage")
            }}
          >
            <UserIcon size={14} /> 내 프로필
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              setOpen(false)
              logout()
            }}
          >
            <LogOut size={14} /> 로그아웃
          </DropdownItem>
        </Dropdown>
      )}
    </UserMenuWrapper>
  )
}

export default function Header({
  zoomLevel,
  setZoomLevel,
  viewMode,
  setViewMode,
  setIsSaveModalOpen,
  fetchSavedCharts,
  setIsLoadModalOpen,
  handleExportJson,
  handleFileImport,
  setIsMembersModalOpen,
  currentFileName,
  handleSave,
  handleNewFile,
  setIsColorModalOpen,
  setIsShareModalOpen,
  setIsAlarmModalOpen,
  currentChartType, // 'personal' | 'team'
  handleGoHome, // 대시보드(환영 화면)로 이동
  currentFileId,
  chartTheme,
  setChartTheme,
  referenceDate,
  setReferenceDate,
  isDashboardEmpty,
  onToggleSidebar, // 모바일에서 사이드바 토글
  onOpenDrawer, // 좌측 글로벌 드로어 열기
  activeTab,
  onAddTask,
  onAddMilestone,
  searchQuery,
  setSearchQuery,
}) {
  const fileInputRef = useRef(null)
  const [moreOpen, setMoreOpen] = useState(false)
  const moreRef = useRef(null)

  useEffect(() => {
    if (!moreOpen) return
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [moreOpen])
  const toYmd = (d) => {
    const dt = d ? new Date(d) : new Date()
    const y = dt.getFullYear()
    const m = String(dt.getMonth() + 1).padStart(2, '0')
    const day = String(dt.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  const refYmd = toYmd(referenceDate)
  const todayYmd = toYmd(new Date())
  const isRefToday = refYmd === todayYmd

  const handleSaveClick = () => {
    if (currentFileName) handleSave()
    else setIsSaveModalOpen(true)
  }

  return (
    <HeaderContainer data-cursor-anchor="header">
      {/* === Top: 정보 줄 (햄버거 · 제목 · 배지 · 팀상태) | (알람 · 초대 · 프로필) === */}
      <TopRow>
        <TopLeft>
          {onOpenDrawer && (
            <HamburgerButton onClick={onOpenDrawer} title="메뉴" aria-label="메뉴">
              <Menu size={20} />
            </HamburgerButton>
          )}
          <ProjectTitle>
            <span className="title">{currentFileName || "간트차트"}</span>
            {currentFileId && currentChartType && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 9px',
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 700,
                background: currentChartType === 'team' ? '#dbeafe' : '#f1f5f9',
                color: currentChartType === 'team' ? '#1e40af' : '#475569',
                border: `1px solid ${currentChartType === 'team' ? '#bfdbfe' : '#e5e7eb'}`,
              }}>
                {currentChartType === 'team'
                  ? (<><TeamIcon size={11}/> 팀</>)
                  : (<><PersonIcon size={11}/> 개인</>)}
              </span>
            )}
          </ProjectTitle>
          {currentChartType === 'team' && <PresenceBar />}
        </TopLeft>
        <TopCenter>
          {!isDashboardEmpty && (
            <ViewTabs role="tablist" aria-label="뷰 전환">
              <ViewTab role="tab" aria-selected={chartTheme === "classic"} $active={chartTheme === "classic"} onClick={() => setChartTheme("classic")}>
                타임라인
              </ViewTab>
              <ViewTab role="tab" aria-selected={chartTheme === "wbs"} $active={chartTheme === "wbs"} onClick={() => setChartTheme("wbs")}>
                WBS
              </ViewTab>
              <ViewTab role="tab" aria-selected={chartTheme === "report"} $active={chartTheme === "report"} onClick={() => setChartTheme("report")}>
                보고서
              </ViewTab>
            </ViewTabs>
          )}
        </TopCenter>
        <TopRight>
          {!isDashboardEmpty && setSearchQuery && (
            <SearchBox>
              <Search size={14} className="icon" />
              <input
                type="text"
                placeholder="검색 (작업, 담당자)"
                value={searchQuery || ""}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button type="button" className="clear" onClick={() => setSearchQuery("")} aria-label="검색 지우기">
                  <X size={11} />
                </button>
              )}
            </SearchBox>
          )}
          {!isDashboardEmpty && setIsAlarmModalOpen && (
            <IconButton onClick={() => setIsAlarmModalOpen(true)} title="앤츠톡 푸시 알람 설정">
              <Bell size={18} />
            </IconButton>
          )}
          {currentChartType === 'team' && setIsShareModalOpen && currentFileId && (
            <ShareButton onClick={() => setIsShareModalOpen(true)} title="팀 멤버 초대">
              <UserPlus size={16} /> <span className="btn-label">초대</span>
            </ShareButton>
          )}
          <UserMenu />
        </TopRight>
      </TopRow>

      {/* === Bottom: 뷰 탭 + 액션 | 줌·뷰모드·기준일 === */}
      <BottomRow>
        <ButtonGroup>
          {handleGoHome && !isDashboardEmpty && (
            <Button onClick={handleGoHome} title="홈(대시보드)으로">
              <Home size={16} /> <span className="btn-label">홈</span>
            </Button>
          )}
          <Button onClick={handleNewFile}>
            <FilePlus size={16} /> <span className="btn-label">새 차트</span>
          </Button>
          <Button onClick={handleSaveClick}>
            <Save size={16} /> <span className="btn-label">{currentFileName ? "저장" : "다른 이름으로 저장"}</span>
          </Button>
          {isDashboardEmpty && (
            <Button onClick={() => { fetchSavedCharts(); setIsLoadModalOpen(true); }}>
              <FolderOpen size={16} /> <span className="btn-label">불러오기</span>
            </Button>
          )}
          {/* 불러오기/내보내기는 차트 작업 중일 때 사이드바 하단으로 이동 */}
          <input type="file" ref={fileInputRef} accept=".json" style={{ display: "none" }} onChange={handleFileImport} />
          {isDashboardEmpty && (
            <Button onClick={() => fileInputRef.current?.click()}>
              <Upload size={16} /> <span className="btn-label">가져오기</span>
            </Button>
          )}
          {!isDashboardEmpty && (
            <>
              <DesktopOnly>
                <Button onClick={() => setIsMembersModalOpen(true)}>
                  <Users size={16} /> 담당자 관리
                </Button>
                <Button onClick={() => setIsColorModalOpen(true)}>
                  <Palette size={16} /> 색상 설정
                </Button>
              </DesktopOnly>
              <MoreMenuWrap ref={moreRef}>
                <MoreMenuBtn onClick={() => setMoreOpen(v => !v)} title="더보기" aria-label="더보기">
                  <MoreVertical size={18} />
                </MoreMenuBtn>
                {moreOpen && (
                  <MoreMenuDropdown>
                    <button onClick={() => { setMoreOpen(false); setIsMembersModalOpen(true) }}>
                      <Users size={14} /> 담당자 관리
                    </button>
                    <button onClick={() => { setMoreOpen(false); setIsColorModalOpen(true) }}>
                      <Palette size={14} /> 색상 설정
                    </button>
                  </MoreMenuDropdown>
                )}
              </MoreMenuWrap>
            </>
          )}
        </ButtonGroup>

        <ControlGroup>
          {chartTheme === "classic" && !isDashboardEmpty && (
            <>
              <ZoomControls>
                <IconButton onClick={() => setZoomLevel(Math.max(0.2, zoomLevel - 0.1))}>
                  <ZoomOut size={16} />
                </IconButton>
                <Slider
                  value={zoomLevel}
                  min={0.1}
                  max={5}
                  step={0.1}
                  onChange={(event) => setZoomLevel(Number.parseFloat(event.target.value))}
                />
                <IconButton onClick={() => setZoomLevel(Math.min(5, zoomLevel + 0.1))}>
                  <ZoomIn size={16} />
                </IconButton>
              </ZoomControls>

              <Select value={viewMode} onChange={(event) => setViewMode(event.target.value)}>
                <option value="day">일 단위</option>
                <option value="week">주 단위</option>
                <option value="month">월 단위</option>
              </Select>
            </>
          )}

          {/* 기준일은 StatsStrip 상단으로 이동 */}
        </ControlGroup>
      </BottomRow>
    </HeaderContainer>
  )
}
